import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Shield, Clock, Lock, Zap, ChevronDown, Minus, Info } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useAuth } from "@/contexts/AuthContext";
import UpgradeQuoteModal from "@/components/UpgradeQuoteModal";
import SubscriptionCelebrationModal from "@/components/SubscriptionCelebrationModal";
import SlotsLeftTicker from "@/components/SlotsLeftTicker";

const TIERS = [
  {
    id: "free",
    name: "Free",
    tagline: "See exactly where your money goes",
    price: { monthly: 0, annual: 0 },
    priceSuffix: { monthly: "forever", annual: "forever" },
    badge: null,
    benefits: [
      "Full intelligence dashboard on your first 10 invoices",
      "Category & platform spending breakdown",
      "Top items analysis with reorder patterns",
      "Your personalised savings estimate",
      "Smart weekly grocery list — first one free",
    ],
    fomo:
      "Most first-time users discover they are overspending by ₹3,000–8,000 every month without realising it.",
    cta: "Analyse My Orders Free →",
    trial: null,
    accent: "#888888",
    scrollTarget: "upload",
  },
  {
    id: "starter",
    name: "Starter",
    tagline: "For smart grocery planners",
    price: { monthly: 69, annual: 690 },
    priceSuffix: { monthly: "/month", annual: "/year" },
    annualNote: "Save ₹138",
    badge: null,
    benefits: [
      "Unlimited invoice uploads — Blinkit, Zepto, Swiggy Instamart",
      "Smart weekly grocery list every Saturday morning (Coming soon)",
      "WhatsApp budget alerts at 60%, 80% and 100% of your budget (Coming soon)",
      "\"This is my budget\" — set your monthly food budget and track it live",
      "Restock predictions — never run out of essentials again",
      "Personalised savings recommendations every week",
    ],
    fomo:
      "Average Starter user saves ₹4,200/month — that is 60x the cost of this subscription, or skipping just one cup of cutting chai a week.",
    cta: "Start Free Trial →",
    trial: "Starter free for 15 days · No credit card needed",
    accent: "#378ADD",
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Complete food intelligence across every app",
    price: { monthly: 99, annual: 990 },
    priceSuffix: { monthly: "/month", annual: "/year" },
    annualNote: "Save ₹198",
    badge: { label: "MOST POPULAR", bg: "#FF6B00", fg: "#FFFFFF" },
    benefits: [
      "Everything in Starter",
      "All platforms — Zomato, Swiggy Food, Uber Eats, BigBasket",
      "Unified 360 food report — grocery + delivery in one view",
      "Cook vs Order savings calculator (Coming soon)",
      "FMCG price comparison — same item, best price across apps (Coming soon)",
      "Restaurant intelligence — your cuisine patterns and habits",
      "Emotional spending alerts — late night orders, weekend splurges",
      "Monthly downloadable PDF intelligence report",
    ],
    fomo:
      "Pro users unlock an average ₹7,800/month in food intelligence. The subscription pays for itself in under half a day — less than the price of one café latte.",
    socialProof: "Joined by 500+ urban Indian families this month",
    cta: "Get Pro Free for 15 Days →",
    trial: "Pro free for 15 days · Cancel anytime",
    accent: "#FF6B00",
    highlight: true,
  },
  {
    id: "family",
    name: "Family",
    tagline: "One dashboard for the whole household",
    price: { monthly: 149, annual: 1490 },
    priceSuffix: { monthly: "/month", annual: "/year" },
    annualNote: "Save ₹298",
    badge: { label: "COMING SOON", bg: "#1D9E75", fg: "#FFFFFF" },
    benefits: [
      "Everything in Pro for up to 5 family members",
      "Shared family food budget dashboard",
      "Per-member spending breakdown and insights",
      "Family savings report — combined household intelligence",
      "Early access to all new features",
    ],
    fomo:
      "At under ₹30 per person per month, CartIQ Family costs less than one weekend Zomato side dish. Families on CartIQ reduce food waste by 31% on average.",
    cta: "Coming soon",
    trial: "Family plan launching soon · drop in for early access",
    accent: "#1D9E75",
    comingSoon: true,
  },
];

const TRUST = [
  { icon: Clock, label: "15-day free trial on all paid plans" },
  { icon: Shield, label: "Cancel anytime, no questions asked" },
  { icon: Lock, label: "Your data is private and never sold" },
  { icon: Zap, label: "Works with Blinkit, Zepto, Zomato, Swiggy & more" },
];

const FAQS = [
  {
    q: "Do I need to download an app?",
    a: "No. CartIQ 360 works entirely in your browser and on WhatsApp. Nothing to install.",
  },
  {
    q: "How do I upload my orders?",
    a: "Download the PDF invoice from any past Blinkit, Zepto or Zomato order and drop it on our upload page. Takes 2 minutes.",
  },
  {
    q: "Is my spending data safe?",
    a: "Yes. Your data is encrypted, stored securely, and never shared with any third party including the food apps themselves.",
  },
  {
    q: "What if I order from multiple apps?",
    a: "Perfect — that is exactly what CartIQ 360 is built for. Upload invoices from any combination of apps and we build your unified report.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel in one tap. No notice period, no cancellation fee, no questions asked.",
  },
];

const formatINR = (n) => `₹${new Intl.NumberFormat("en-IN").format(n)}`;

// A6 (UAT V2 obs #18): value-add explanation for every line item across
// Starter / Pro / Family. Keyed by lowercased substrings that appear in the
// benefit copy so we don't have to restructure the TIERS array.
// Surfaced as a hover tooltip on a small info icon — never crowds the card.
const BENEFIT_DESCRIPTIONS = [
  // Free tier
  { match: "first 10 invoices", desc: "Drop your first 10 PDFs or screenshots — no signup, no credit card. The full intelligence dashboard unlocks the moment you upload." },
  { match: "category & platform", desc: "Doughnut + bar charts show exactly where each rupee went — by food category (Dairy, Vegetables, Snacks…) and by platform (Blinkit, Zepto, Zomato…)." },
  { match: "top items", desc: "See your 10 most-frequent items, total spend on each, and predicted reorder dates — so you never run out of essentials." },
  { match: "personalised savings", desc: "An honest, data-backed estimate of how much you can save — derived from your actual ordering patterns, not generic benchmarks." },
  { match: "first one free", desc: "Your first weekly grocery list — the 12-16 staples our AI predicts you'll need next week, hand-picked from your purchase history." },
  // Starter
  { match: "unlimited invoice uploads", desc: "Drop as many PDFs and screenshots as you like — no monthly cap, no lifetime cap. Every upload feeds the cumulative dashboard." },
  { match: "smart weekly grocery list every saturday", desc: "Every Saturday at 9 AM, your AI-curated grocery list lands on WhatsApp — sized to your weekly budget, balanced across categories." },
  { match: "whatsapp budget alerts", desc: "Three smart nudges per month — 60% / 80% / 100% of your budget — so you slow down before overspending, not after." },
  { match: "this is my budget", desc: "Lock in your monthly food budget. We track spend live and project forward — you always know if you're on track or off." },
  { match: "restock predictions", desc: "We learn how often you reorder Amul Milk, Atta, Eggs etc. and ping you a day before you typically run out." },
  { match: "personalised savings recommendations", desc: "Every week we point to the one habit change worth ~₹500-1,500/month — measurable, doable, no guilt-tripping." },
  { match: "cancel anytime", desc: "One tap. No notice period, no fee, no email loops. Your data stays yours either way." },
  // Pro
  { match: "everything in starter", desc: "All Starter features carry over — unlimited uploads, Saturday lists, budget alerts, restock predictions and savings tips." },
  { match: "all platforms", desc: "Pro adds Zomato, Swiggy Food, Uber Eats and BigBasket on top of the grocery apps — so your dashboard sees every food rupee." },
  { match: "unified 360 food report", desc: "One single view of grocery + restaurant + dining-out spend. Stop juggling five apps to understand your real food bill." },
  { match: "cook vs order", desc: "An AI-driven calculator that quantifies the rupee gap between cooking at home and ordering in — translated into concrete monthly savings." },
  { match: "fmcg price comparison", desc: "Same ₹120 Amul Butter — sometimes ₹109 on Blinkit, ₹118 on Zepto, ₹128 on BigBasket. Pro shows the cheapest source for every staple, every week." },
  { match: "restaurant intelligence", desc: "Cuisine breakdown, repeat-restaurant patterns, late-night vs lunch behaviour — your restaurant DNA, mapped." },
  { match: "emotional spending alerts", desc: "We flag impulse-prone windows — late nights, post-meeting splurges, weekend binges — so you spot the pattern before it spots you." },
  { match: "monthly downloadable pdf", desc: "A polished, branded monthly PDF intelligence report — perfect for sharing with your spouse, planner or financial advisor." },
  { match: "priority whatsapp support", desc: "Skip the queue. Pro users get first-line response on WhatsApp from a real human, typically within 4 working hours." },
  // Family
  { match: "everything in pro for up to 5", desc: "Every Pro feature, scaled to five members under one household. Each member uploads independently, results merge into one dashboard." },
  { match: "shared family food budget", desc: "One household budget, contributions from every member visible — so the whole family sees who's spending what, where." },
  { match: "per-member spending breakdown", desc: "Drill into each family member's spend, categories, top platforms — without needing four separate logins." },
  { match: "individual whatsapp connections", desc: "Each family member links their own number — gets their own Saturday list and budget alerts on WhatsApp, in their own language preference." },
  { match: "family savings report", desc: "A combined household intelligence brief, delivered weekly — total saved, total wasted, biggest opportunity." },
  { match: "kids meal pattern", desc: "Coming soon: track snacks, sugary drinks, and protein/carb skew across kids' orders so you can intervene early." },
  { match: "dedicated account manager", desc: "A real person assigned to your household — for setup help, plan changes, and bespoke savings reviews on demand." },
];

const findBenefitDesc = (text) => {
  const t = (text || "").toLowerCase();
  const hit = BENEFIT_DESCRIPTIONS.find((d) => t.includes(d.match));
  return hit ? hit.desc : null;
};

const COMPARE_GROUPS = [
  {
    group: "Intelligence & analytics",
    rows: [
      { feature: "Invoice uploads", values: ["5 lifetime", "Unlimited", "Unlimited", "Unlimited"] },
      { feature: "Category & platform breakdown", values: [true, true, true, true] },
      { feature: "Top items + reorder patterns", values: [true, true, true, true] },
      { feature: "Food timeline (month × category trend)", values: [true, true, true, true] },
      { feature: "Personalised savings estimate", values: [true, true, true, true] },
    ],
  },
  {
    group: "Apps covered",
    rows: [
      { feature: "Grocery — Blinkit, Zepto, Swiggy Instamart", values: [true, true, true, true] },
      { feature: "Food delivery — Zomato, Swiggy, Uber Eats, BigBasket", values: [false, false, true, true] },
      { feature: "Unified 360 food report (grocery + delivery)", values: [false, false, true, true] },
    ],
  },
  {
    group: "WhatsApp automation (Coming soon)",
    rows: [
      { feature: "Saturday 9 AM smart grocery list", values: ["Coming soon", "Coming soon", "Coming soon", "Coming soon"] },
      { feature: "Budget alerts at 60 / 80 / 100 %", values: [false, "Coming soon", "Coming soon", "Coming soon"] },
      { feature: "Restock predictions & reminders", values: [false, "Coming soon", "Coming soon", "Coming soon"] },
    ],
  },
  {
    group: "Pro intelligence",
    rows: [
      { feature: "Cook vs Order savings calculator", values: [false, false, "Coming soon", "Coming soon"] },
      { feature: "FMCG price comparison across apps", values: [false, false, "Coming soon", "Coming soon"] },
      { feature: "Restaurant & cuisine pattern analysis", values: [false, false, true, true] },
      { feature: "Emotional spending alerts", values: [false, false, true, true] },
      { feature: "Monthly downloadable PDF report", values: [false, false, true, true] },
    ],
  },
  {
    group: "Family",
    rows: [
      { feature: "Members supported", values: ["1", "1", "1", "Up to 5"] },
      { feature: "Shared family dashboard", values: [false, false, false, true] },
      { feature: "Per-member insights", values: [false, false, false, true] },
      { feature: "Early access to new features", values: [false, false, false, true] },
    ],
  },
];

export default function PricingSection({ onFreeCtaClick }) {
  const [cycle, setCycle] = useState("monthly"); // 'monthly' | 'annual'
  const [paying, setPaying] = useState(null); // tierId currently being charged
  const [livePrices, setLivePrices] = useState(null);
  // E1: prorated upgrade quote modal target { plan, cycle }
  const [quoteTarget, setQuoteTarget] = useState(null);
  // E2: celebration modal payload { plan, isUpgrade, fromPlan }
  const [celebration, setCelebration] = useState(null);
  const { user, refresh } = useAuth();

  // Pull the canonical price catalogue from the backend so test-mode
  // overrides (PLAN_PRICE_*_MONTHLY env vars) automatically apply to the UI.
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        const cat = d?.catalogue || d?.plans;
        if (cat) setLivePrices(cat);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const tiers = useMemo(() => {
    if (!livePrices) return TIERS;
    return TIERS.map((t) => {
      const live = livePrices[t.id];
      if (!live) return t;
      return {
        ...t,
        price: {
          monthly: live.monthly ?? t.price.monthly,
          annual: live.yearly ?? t.price.annual,
        },
      };
    });
  }, [livePrices]);

  const handlePaidCta = async (tierId) => {
    if (tierId === "free") {
      if (onFreeCtaClick) onFreeCtaClick();
      return;
    }
    if (tierId === "family") {
      toast.info("Family plan is launching soon. We'll email you the moment it's live.");
      return;
    }
    if (!user) {
      toast.info("Sign in with Google first to start your 15-day free trial.");
      return;
    }
    const rzpCycle = cycle === "annual" ? "yearly" : "monthly";
    const userPlan = (user?.plan || "free").toLowerCase();
    // E1: paid users get the prorated quote modal first.
    if (userPlan !== "free") {
      setQuoteTarget({ plan: tierId, cycle: rzpCycle });
      return;
    }
    await launchCheckout(tierId, rzpCycle);
  };

  const launchCheckout = async (tierId, rzpCycle) => {
    setPaying(tierId);
    try {
      await openRazorpayCheckout(
        { plan: tierId, cycle: rzpCycle },
        {
          onSuccess: (verify) => {
            // E2: celebrate before refreshing auth so the modal mounts at once.
            setCelebration({
              plan: verify.plan,
              isUpgrade: verify.isUpgrade,
              fromPlan: verify.fromPlan,
            });
            refresh?.();
          },
        }
      );
    } catch (e) {
      if (
        e?.message !== "dismissed" &&
        e?.code !== "already_on_plan" &&
        e?.code !== "downgrade_not_supported"
      ) {
        console.error("razorpay checkout error", e);
      }
    } finally {
      setPaying(null);
    }
  };

  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-14 md:py-20" data-testid="pricing-section">
      {/* E1: Prorated upgrade preview shown for paid users */}
      <UpgradeQuoteModal
        open={Boolean(quoteTarget)}
        plan={quoteTarget?.plan || "pro"}
        cycle={quoteTarget?.cycle || "monthly"}
        onClose={() => setQuoteTarget(null)}
        onConfirm={async () => {
          const t = quoteTarget;
          setQuoteTarget(null);
          if (t) await launchCheckout(t.plan, t.cycle);
        }}
      />
      {/* E2: Celebration on subscription success */}
      <SubscriptionCelebrationModal
        open={Boolean(celebration)}
        plan={celebration?.plan}
        isUpgrade={celebration?.isUpgrade}
        fromPlan={celebration?.fromPlan}
        onClose={() => setCelebration(null)}
      />

      {/* Eyebrow + intro */}
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-2">
        — Simple pricing
      </div>
      <h2 className="font-display uppercase text-3xl md:text-4xl mb-3">
        Pay for the intelligence. Keep the savings.
      </h2>
      <p className="text-sm md:text-base text-[#888] max-w-2xl mb-5 leading-relaxed">
        Join <span className="text-[#F0EDE8] font-bold">2,000+ Indian families</span> who stopped
        guessing and started knowing exactly how they eat.
      </p>
      <div className="mb-8" data-testid="pricing-slots-ticker">
        <SlotsLeftTicker compact />
      </div>

      {/* Billing cycle toggle */}
      <div
        className="inline-flex p-1 bg-[#161620] border border-[#2A2A35] mb-10"
        role="tablist"
        data-testid="pricing-cycle-toggle"
      >
        <button
          type="button"
          role="tab"
          aria-selected={cycle === "monthly"}
          className={`px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold transition-colors ${
            cycle === "monthly" ? "bg-[#FF6B00] text-white" : "text-[#888] hover:text-[#F0EDE8]"
          }`}
          onClick={() => setCycle("monthly")}
          data-testid="cycle-monthly"
        >
          Monthly
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={cycle === "annual"}
          className={`px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold transition-colors flex items-center gap-2 ${
            cycle === "annual" ? "bg-[#FF6B00] text-white" : "text-[#888] hover:text-[#F0EDE8]"
          }`}
          onClick={() => setCycle("annual")}
          data-testid="cycle-annual"
        >
          Annual
          <span
            className={`text-[9px] px-1.5 py-0.5 ${
              cycle === "annual"
                ? "bg-white/20 text-white"
                : "bg-[#1D9E75]/20 text-[#1D9E75]"
            }`}
          >
            SAVE 25%
          </span>
        </button>
      </div>

      {/* Tier cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {tiers.map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            cycle={cycle}
            paying={paying === tier.id}
            onCta={() => handlePaidCta(tier.id)}
          />
        ))}
      </div>

      {/* Compare features table */}
      <CompareTable
        onPaidCta={(tierId) => handlePaidCta(tierId)}
        paying={paying}
      />

      {/* Trust strip */}
      <div
        className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-t border-b border-[#2A2A35]"
        data-testid="trust-strip"
      >
        {TRUST.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-start gap-2.5">
            <div className="w-7 h-7 bg-[#1D9E75]/10 border border-[#1D9E75]/30 flex items-center justify-center shrink-0">
              <Icon className="w-3.5 h-3.5 text-[#1D9E75]" />
            </div>
            <div className="text-xs text-[#F0EDE8] leading-snug pt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div className="mt-16" data-testid="faq-section">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-2">
          — Questions we hear most
        </div>
        <h3 className="font-display uppercase text-2xl md:text-3xl mb-6">Quick answers.</h3>
        <Accordion type="single" collapsible className="border border-[#2A2A35] bg-[#161620]">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`faq-${i}`}
              className="border-b border-[#2A2A35] last:border-b-0"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="px-5 py-4 text-left hover:no-underline hover:text-[#FF6B00] text-[#F0EDE8] font-bold uppercase tracking-wide text-sm">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-4 text-sm text-[#888] leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

const TierCard = ({ tier, cycle, onCta, paying }) => {
  const price = tier.price[cycle];
  const suffix = tier.priceSuffix[cycle];
  const isFree = tier.id === "free";
  const isHighlight = tier.highlight;
  const ringStyle = isHighlight
    ? { borderColor: tier.accent, boxShadow: `0 0 0 1px ${tier.accent}` }
    : {};

  return (
    <div
      className="relative pt-4"
      data-testid={`tier-card-${tier.id}`}
    >
      {/* Badge */}
      {tier.badge && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-0 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] z-10"
          style={{ background: tier.badge.bg, color: tier.badge.fg }}
          data-testid={`badge-${tier.id}`}
        >
          {tier.badge.label}
        </div>
      )}

      <div
        className="cartiq-card h-full flex flex-col"
        style={ringStyle}
      >
        {/* Top accent bar */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{
            background: `linear-gradient(90deg, ${tier.accent} 0%, ${tier.accent} 70%, transparent 100%)`,
          }}
        />

        <div
          className="text-[11px] uppercase tracking-[0.22em] font-bold mb-1.5"
          style={{ color: tier.accent }}
        >
          {tier.name}
        </div>
        <div className="text-sm text-[#888] mb-5 leading-snug min-h-[2.5rem]">
          {tier.tagline}
        </div>

        {/* Price */}
        <div className="mb-2 flex items-baseline gap-2">
          <div className="font-mono-num text-4xl text-[#F0EDE8]">
            {isFree ? "₹0" : formatINR(price)}
          </div>
          {!isFree && (
            <div className="text-xs text-[#888] uppercase tracking-wider">{suffix}</div>
          )}
        </div>
        {tier.annualNote && cycle === "annual" && !isFree && (
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#1D9E75] font-bold mb-5">
            {tier.annualNote}
          </div>
        )}
        {isFree && <div className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-5">Forever free</div>}
        {!isFree && !tier.annualNote && <div className="h-[18px] mb-5" />}

        {/* Benefits */}
        <ul className="space-y-2 mb-5 flex-1">
          {tier.benefits.map((b, i) => {
            const desc = findBenefitDesc(b);
            return (
              <li key={i} className="flex items-start gap-2 text-[13px] text-[#F0EDE8] leading-relaxed">
                <Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: tier.accent }} />
                <span className="flex-1">{b}</span>
                {desc && (
                  <span
                    className="relative group shrink-0 mt-0.5"
                    data-testid={`benefit-info-${tier.id}-${i}`}
                  >
                    <Info
                      className="w-3.5 h-3.5 text-[#555] hover:text-[#FF6B00] transition-colors cursor-help"
                      tabIndex={0}
                      aria-label={`What is included: ${b}`}
                    />
                    {/* Hover/focus tooltip — pure CSS, no extra deps */}
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute z-30 right-0 top-full mt-1 w-64 p-3 bg-[#0A0A0F] border border-[#FF6B00]/40 text-[12px] leading-relaxed text-[#F0EDE8] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity shadow-xl"
                      style={{ boxShadow: "0 10px 40px rgba(0,0,0,0.6)" }}
                    >
                      <span
                        className="block text-[10px] uppercase tracking-[0.18em] font-bold mb-1.5"
                        style={{ color: tier.accent }}
                      >
                        What this gets you
                      </span>
                      {desc}
                    </span>
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {/* FOMO box */}
        <div
          className="mb-3 p-3 border-l-[3px]"
          style={{
            background: `${tier.accent}12`,
            borderLeftColor: tier.accent,
          }}
          data-testid={`fomo-${tier.id}`}
        >
          <div className="text-[13px] italic text-[#F0EDE8] leading-relaxed" style={{ fontFamily: "'Georgia', serif" }}>
            "{tier.fomo}"
          </div>
        </div>

        {/* Social proof */}
        {tier.socialProof && (
          <div
            className="text-[11px] uppercase tracking-[0.18em] mb-3 flex items-center gap-2"
            style={{ color: tier.accent }}
            data-testid={`social-proof-${tier.id}`}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tier.accent }} />
            {tier.socialProof}
          </div>
        )}

        {/* CTA */}
        <button
          type="button"
          onClick={onCta}
          disabled={paying || tier.comingSoon}
          className="w-full font-bold uppercase tracking-wider py-3 text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: tier.comingSoon ? "transparent" : isHighlight ? tier.accent : "transparent",
            color: tier.accent,
            border: `1px solid ${tier.accent}${tier.comingSoon ? "55" : ""}`,
          }}
          onMouseEnter={(e) => {
            if (paying || tier.comingSoon) return;
            e.currentTarget.style.background = tier.accent;
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            if (tier.comingSoon) return;
            if (!isHighlight && !paying) {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = tier.accent;
            }
          }}
          data-testid={`cta-${tier.id}`}
        >
          {paying ? "Opening Razorpay…" : tier.cta}
        </button>

        {/* Trial */}
        {tier.trial && (
          <div
            className="text-[10px] uppercase tracking-[0.18em] text-[#555] text-center mt-2"
            data-testid={`trial-text-${tier.id}`}
          >
            {tier.trial}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Compare features table ---
const COMPARE_COLS = [
  { id: "free", label: "Free", accent: "#888888" },
  { id: "starter", label: "Starter", accent: "#378ADD" },
  { id: "pro", label: "Pro", accent: "#FF6B00", highlight: true },
  { id: "family", label: "Family", accent: "#1D9E75" },
];

const CompareCell = ({ value, accent }) => {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <span
          className="w-7 h-7 flex items-center justify-center"
          style={{ background: `${accent}18`, border: `1px solid ${accent}60` }}
        >
          <Check className="w-3.5 h-3.5" style={{ color: accent }} />
        </span>
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-center">
        <span className="w-7 h-7 flex items-center justify-center text-[#2A2A35]">
          <Minus className="w-4 h-4" />
        </span>
      </div>
    );
  }
  return (
    <div
      className="text-center text-xs font-bold uppercase tracking-[0.12em]"
      style={{ color: accent }}
    >
      {value}
    </div>
  );
};

const CompareTable = ({ onPaidCta, paying }) => {
  return (
    <div className="mt-16 md:mt-20" data-testid="compare-table-section">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-2">
        — Compare every feature
      </div>
      <h3 className="font-display uppercase text-2xl md:text-3xl mb-6">
        What's in every plan, side by side.
      </h3>

      <div
        className="border border-[#2A2A35] bg-[#0F0F14] overflow-x-auto"
        data-testid="compare-table-wrapper"
      >
        <table className="w-full min-w-[720px] text-sm" data-testid="compare-table">
          {/* Sticky header with plan columns */}
          <thead>
            <tr className="bg-[#161620] border-b border-[#2A2A35]">
              <th className="text-left p-4 font-bold uppercase tracking-[0.18em] text-[11px] text-[#888] w-[38%]">
                Features
              </th>
              {COMPARE_COLS.map((c) => (
                <th
                  key={c.id}
                  className="p-4 text-center relative"
                  data-testid={`compare-col-${c.id}`}
                >
                  {c.highlight && (
                    <div
                      className="absolute top-0 left-0 right-0 h-[2px]"
                      style={{ background: c.accent }}
                    />
                  )}
                  <div
                    className="font-display uppercase text-base md:text-lg"
                    style={{ color: c.accent }}
                  >
                    {c.label}
                  </div>
                  {c.highlight && (
                    <div className="text-[9px] uppercase tracking-[0.22em] text-[#FF6B00] mt-1">
                      Most popular
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {COMPARE_GROUPS.map((g) => (
              <React.Fragment key={g.group}>
                <tr className="bg-[#0A0A0F]">
                  <td
                    colSpan={5}
                    className="px-4 py-3 text-[11px] uppercase tracking-[0.22em] font-bold text-[#FF6B00] border-y border-[#2A2A35]"
                    data-testid={`compare-group-${g.group.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {g.group}
                  </td>
                </tr>
                {g.rows.map((row, ri) => (
                  <tr
                    key={`${g.group}-${ri}`}
                    className="border-b border-[#2A2A35] last:border-b-0 hover:bg-[#161620]/40 transition-colors"
                    data-testid={`compare-row-${g.group.replace(/\s+/g, "-").toLowerCase()}-${ri}`}
                  >
                    <td className="px-4 py-3.5 text-[#F0EDE8] leading-snug">
                      {row.feature}
                    </td>
                    {row.values.map((v, vi) => (
                      <td key={vi} className="px-4 py-3.5">
                        <CompareCell value={v} accent={COMPARE_COLS[vi].accent} />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}

            {/* CTA footer row */}
            <tr className="bg-[#0F0F14] border-t border-[#2A2A35]">
              <td className="px-4 py-5 text-[11px] uppercase tracking-[0.22em] text-[#888] font-bold">
                Ready when you are
              </td>
              {COMPARE_COLS.map((c) => {
                const isComingSoon = c.id === "family";
                return (
                <td key={c.id} className="px-3 py-5">
                  <button
                    type="button"
                    onClick={() => onPaidCta(c.id)}
                    disabled={paying === c.id || isComingSoon}
                    className="w-full font-bold uppercase tracking-wider py-2.5 text-[11px] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      background: !isComingSoon && c.highlight ? c.accent : "transparent",
                      color: !isComingSoon && c.highlight ? "#fff" : c.accent,
                      border: `1px solid ${c.accent}${isComingSoon ? "55" : ""}`,
                    }}
                    onMouseEnter={(e) => {
                      if (paying === c.id || isComingSoon) return;
                      e.currentTarget.style.background = c.accent;
                      e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      if (isComingSoon) return;
                      if (!c.highlight && paying !== c.id) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = c.accent;
                      }
                    }}
                    data-testid={`compare-cta-${c.id}`}
                  >
                    {isComingSoon
                      ? "Coming soon"
                      : paying === c.id
                      ? "Opening…"
                      : c.id === "free"
                      ? "Start free"
                      : "Start trial"}
                  </button>
                </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="text-[11px] uppercase tracking-[0.18em] text-[#555] mt-3 flex items-center gap-2">
        <Lock className="w-3 h-3" />
        All paid plans include a 15-day free trial · cancel anytime · no credit card needed
      </div>
    </div>
  );
};
